/**
 * Helpers for WhatsApp template CTA buttons with dynamic (variable) URLs.
 * ButtonMetadata contract (dynamic): { type: "dynamicURL", text, url, example }
 */

export function parseButtonMetadata(buttonMetadata) {
	if (!buttonMetadata) return [];
	try {
		const parsed = JSON.parse(buttonMetadata);
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
}

export function isDynamicUrlButton(button) {
	if (!button) return false;
	if (button.type === "dynamicURL") return true;
	if (button.type === "URL") {
		const url = button.url || "";
		if (/\{\{\d+\}\}/.test(url)) return true;
		if (url.endsWith("/(1)")) return true;
		if (/\{1\}$/.test(url)) return true;
	}
	return false;
}

/** URL prefix stored in metadata, always ending with {{1}} when possible. */
export function getDynamicUrlPrefix(button) {
	if (button.type === "dynamicURL") {
		return button.url || "";
	}
	const url = button.url || "";
	if (/\{\{\d+\}\}/.test(url)) return url;
	if (url.endsWith("/(1)")) {
		return url.substring(0, url.length - 3) + "{{1}}";
	}
	if (/\{1\}$/.test(url)) {
		return url.replace(/\{1\}$/, "{{1}}");
	}
	return url;
}

export function buildFullDynamicUrl(urlPrefix, variableValue) {
	if (!urlPrefix) return "";
	if (!variableValue) return urlPrefix;
	if (/\{\{1\}\}$/.test(urlPrefix)) {
		return urlPrefix.replace(/\{\{1\}\}$/, variableValue);
	}
	return urlPrefix + variableValue;
}

/** Fixed URL segment from template metadata (everything before `{{n}}`). */
export function getStaticUrlPrefixBeforePlaceholder(urlPrefix) {
	if (!urlPrefix) return "";
	const placeholderMatch = urlPrefix.match(/^(.*)\{\{\d+\}\}/);
	if (placeholderMatch) return placeholderMatch[1];
	return urlPrefix.replace(/\{\{1\}\}$/, "").replace(/\{1\}$/, "");
}

function trimTrailingSlashes(value) {
	return value.replace(/\/+$/, "");
}

function escapeRegExp(string) {
	return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function parseUrlLenient(value, baseUrl) {
	try {
		if (/^[a-z][a-z0-9+.-]*:/i.test(value)) {
			return new URL(value);
		}
		if (baseUrl) {
			return new URL(value, baseUrl);
		}
		return new URL(`https://${value.replace(/^\/+/, "")}`);
	} catch {
		return null;
	}
}

/**
 * If the user entered or pasted a full URL, keep only the variable suffix Meta expects.
 * When the value already is a suffix, it is returned unchanged.
 */
export function stripDynamicUrlVariableSuffix(rawValue, urlPrefix) {
	if (rawValue == null || rawValue === "") return rawValue;
	const value = String(rawValue).trim();
	if (!value || !urlPrefix) return value;

	const staticPrefix = getStaticUrlPrefixBeforePlaceholder(urlPrefix);
	if (!staticPrefix) return value;

	const prefixPattern = new RegExp(
		`^${escapeRegExp(trimTrailingSlashes(staticPrefix))}\\/?`,
		"i"
	);
	const directMatch = value.match(prefixPattern);
	if (directMatch) {
		return value.slice(directMatch[0].length).replace(/^\/+/, "");
	}

	const prefixUrl = parseUrlLenient(staticPrefix);
	const valueUrl = parseUrlLenient(value, prefixUrl || undefined);
	if (!prefixUrl || !valueUrl) return value;

	if (
		valueUrl.protocol.toLowerCase() !== prefixUrl.protocol.toLowerCase() ||
		valueUrl.hostname.toLowerCase() !== prefixUrl.hostname.toLowerCase() ||
		(valueUrl.port || "") !== (prefixUrl.port || "")
	) {
		return value;
	}

	const prefixPath = trimTrailingSlashes(prefixUrl.pathname) || "";
	const valuePath = valueUrl.pathname;
	const valuePathTrimmed = trimTrailingSlashes(valuePath);

	if (valuePathTrimmed === prefixPath) {
		const queryAndHash = `${valueUrl.search}${valueUrl.hash}`;
		return queryAndHash.startsWith("?")
			? queryAndHash.slice(1)
			: queryAndHash;
	}

	if (prefixPath) {
		const pathAfterPrefix = valuePath.startsWith(`${prefixPath}/`)
			? valuePath.slice(prefixPath.length + 1)
			: null;
		if (pathAfterPrefix != null) {
			return `${pathAfterPrefix}${valueUrl.search}${valueUrl.hash}`;
		}
	}

	return value;
}

export function dynamicExampleMatchesPrefix(urlPrefix, exampleUrl) {
	if (!urlPrefix || !exampleUrl) return false;
	const expected = buildFullDynamicUrl(urlPrefix, "sample");
	const base = expected.replace(/sample$/, "");
	return exampleUrl.startsWith(base) && exampleUrl.length > base.length;
}

/**
 * @returns {{ index: number, label: string, urlPrefix: string, example?: string }[]}
 */
export function getDynamicUrlButtonsForSend(buttonMetadata) {
	return parseButtonMetadata(buttonMetadata)
		.map((button, index) => ({ button, index }))
		.filter(({ button }) => isDynamicUrlButton(button))
		.map(({ button, index }) => ({
			index,
			label: button.text || button.title || `Button ${index + 1}`,
			urlPrefix: getDynamicUrlPrefix(button),
			example: button.example,
		}));
}

/**
 * @param {{ index: number, value: string }[]} buttonUrlParams
 * @returns {string | null} JSON string for GraphQL `ButtonParams` TEXT column
 */
export function formatButtonParamsForMutation(buttonUrlParams) {
	if (!buttonUrlParams?.length) return null;
	const params = buttonUrlParams
		.filter((item) => item.value != null && String(item.value).length > 0)
		.map((item) => ({
			index: item.index,
			type: "url",
			value: stripDynamicUrlVariableSuffix(item.value, item.urlPrefix),
		}));
	return params.length ? JSON.stringify(params) : null;
}

/** Safely parse `ButtonParams` TEXT from Message/Broadcast rows. */
export function parseButtonParams(buttonParams) {
	if (buttonParams == null || buttonParams === "") return null;
	if (typeof buttonParams === "object") return buttonParams;
	try {
		const parsed = JSON.parse(buttonParams);
		return Array.isArray(parsed) ? parsed : null;
	} catch {
		return null;
	}
}
